import {
    filters as filtersBlank,
    searchRequest as searchRequestBlank,
    searchResponse as searchResponseBlank,
    searchScheduleResponse as searchScheduleResponseBlank,
} from '~src/utils/flights/flights-blank-states.src'
import {appInstance} from '@/utils/app-accessor'
import {runtimeStore, flightsRuntimeStore} from '~/composables/store-accessor'
import type {Ref} from 'vue'
import {isAfter, isBefore, parseISO} from 'date-fns'
import {UTCToDate} from '@/utils/helpers'
import {salesTermsRsTransformer} from '@/utils/api-helpers'
import {useProductStore} from '~/composables/product-store'

export const useFlightsStore = defineStore('flights', () => {
    function _getBasketItem(offerKey: any) {
        return (
            basket.value.find((item: any) => item.offer.offerKey === offerKey) || {
                searchRequest: searchRequestBlank(),
                offer: {},
            }
        )
    }

    const searchMode = ref(0)
    const searchRequest = ref(searchRequestBlank())
    const scheduleSearchRequest = ref(searchRequestBlank())
    const availabilityRequest = ref(null)

    const searchResponse = ref(searchResponseBlank())
    const searchScheduleResponse = ref(searchScheduleResponseBlank())
    const availabilityResponse = ref(searchResponseBlank())

    const sortFnName = ref('priceAsc')
    const searchExpirationTime: Ref = ref(null)

    const filters: Ref = ref(filtersBlank())
    const scheduleFilters: Ref = ref([filtersBlank()])

    const availableOfferFilters: Ref = ref(filtersBlank())

    const prepareBookRequest: Ref = ref({})
    const prepareBookResponse = ref({})
    const selectedScheduleItems: Ref<any[]> = ref([])

    const basket: Ref<any[]> = ref([])
    const bookingAdditionalOptions: Ref<any[]> = ref([])

    const flightPreferences: Ref = ref(null)

    const mealTypesSeatAllowance: Ref<any[]> = ref([])

    const frequentFlyerCard: Ref<any[]> = ref([])

    const airlines: Ref<any[]> = ref([])

    async function loadAirlines() {
        try {
            const {airlines: _airlines} = await appInstance.$api.airlines.get({})
            airlines.value = _airlines
        } catch (e) {
            airlines.value = []
        }
    }

    function ADD_TO_BASKET(offer: any) {
        const {offerKey} = offer
        basket.value = basket.value.filter(item => isBefore(new Date(), UTCToDate(parseISO(item.offer.expirationTime))))
        if (_getBasketItem(offer.offerKey).offerKey) return
        basket.value.push({
            offer,
            offerKey,
            searchRequest: flightsRuntimeStore.isPriceMode ? searchRequest.value : scheduleSearchRequest.value,
        })
    }

    function REFRESH_BASKET_PRICE({offerKey, prepareBookResponse}: any) {
        const basketItem = _getBasketItem(offerKey)
        if (!basketItem.offerKey) return
        const offer = basketItem.offer
        const {price} = salesTermsRsTransformer(prepareBookResponse.currentSalesTerms)
        if (prepareBookResponse?.paymentByLoyaltyPoint?.paidByLoyalty) {
            // @ts-ignore
            price.loyaltyAmount = prepareBookResponse.paymentByLoyaltyPoint.paidByLoyalty
        }
        offer.currentPrice = price
    }

    function SET_SEARCH_REQUEST(rq: any) {
        searchRequest.value = rq
    }

    function SET_SCHEDULE_SEARCH_REQUEST(rq: any) {
        scheduleSearchRequest.value = rq
    }

    function SET_MEAL_TYPE_SEAT_ALLOWANCE({touristIndex, type, code}: any) {
        const alreadyGot = mealTypesSeatAllowance.value[touristIndex]
        mealTypesSeatAllowance.value[touristIndex] = {...alreadyGot, ...{[type]: code}}
    }

    function DELETE_MEAL_TYPE_SEAT_ALLOWANCE({touristIndex, type}: any) {
        delete mealTypesSeatAllowance.value[touristIndex][type]
    }

    function SET_FREQUENT_FLYER_CARD_NUMBER({index, code}: any) {
        let alreadyGot = frequentFlyerCard.value[index]
        if (alreadyGot) {
            alreadyGot.number = code
            frequentFlyerCard.value[index] = {...alreadyGot}
        }
    }

    function DELETE_FREQUENT_FLYER_CARD_TYPE({index}: any) {
        delete frequentFlyerCard.value[index]
    }

    function SET_FREQUENT_FLYER_CARD_TYPE({index, code}: any) {
        let alreadyGot = frequentFlyerCard.value[index]
        if (alreadyGot) {
            alreadyGot.airlineCode = code
        } else {
            alreadyGot = {
                number: '',
                airlineCode: code,
            }
        }
        frequentFlyerCard.value[index] = {...alreadyGot}
    }

    function SET_AVAILABILITY_REQUEST(rq: any) {
        availabilityRequest.value = rq
    }

    function SET_SEARCH_MODE(val: any) {
        searchMode.value = val
    }

    function RESET_FILTERS() {
        filters.value = filtersBlank()
    }

    function SET_FILTER({key, value}: any) {
        filters.value[key] = value
    }

    function SET_SCHEDULE_FILTER({routeIndex, key, value}: any) {
        scheduleFilters.value[routeIndex][key] = value
    }

    function RESET_SCHEDULE_FILTERS(routeIndex: any) {
        scheduleFilters.value[routeIndex] = {...filtersBlank(), duration: [0, 9000]}
    }

    function SET_AVAILABLE_OFFER_FILTER(data: any) {
        availableOfferFilters.value[data.key] = data.value
    }

    function RESET_AVAILABLE_OFFER_FILTERS() {
        availableOfferFilters.value = filtersBlank()
    }

    function SET_SORT(sort: any) {
        sortFnName.value = sort
    }

    function SET_SEARCH_EXPIRATION_TIME(date: any) {
        searchExpirationTime.value = date
    }

    function SET_PREPARE_BOOK_REQUEST(_prepareBookRequest: any) {
        prepareBookRequest.value = _prepareBookRequest
    }

    function SET_PREPARE_BOOK_REQUEST_PROP({prop, val}: any) {
        prepareBookRequest.value[prop] = val
    }

    function SET_PREPARE_BOOK_RESPONSE(_prepareBookResponse: any) {
        prepareBookResponse.value = _prepareBookResponse
    }

    function SET_SELECTED_SCHEDULE_ITEM({index, offer, categoryClass}: any) {
        selectedScheduleItems.value[index] = {offer, categoryClass}
    }

    function NEW_SEARCH(_searchRequest: any) {
        searchRequest.value = _searchRequest
        filters.value = filtersBlank()
    }

    function RESET() {
        searchRequest.value = searchRequestBlank()
        prepareBookRequest.value = {}
    }

    function NEW_SCHEDULE_SEARCH(searchRequest: any) {
        scheduleSearchRequest.value = searchRequest
        scheduleFilters.value = []
        searchRequest.routes.forEach(() => {
            scheduleFilters.value.push({...filtersBlank(), duration: [0, 9000]})
        })
        selectedScheduleItems.value = []
    }

    function SET_SEARCH_SCHEDULE_RESPONSE(searchResponse: any) {
        searchScheduleResponse.value = searchResponse
    }

    function SET_AVAILABILITY_RESPONSE(searchResponse: any) {
        availabilityResponse.value = searchResponse
    }

    async function prepareBook(rq: any) {
        runtimeStore.SET_BOOKING_ACTIVE(true)
        try {
            SET_PREPARE_BOOK_REQUEST(rq)
            const rs = await appInstance.$api.prepareFlightBook.post(rq)
            SET_PREPARE_BOOK_RESPONSE(rs)
            return rs
        } catch (e: any) {
            if (e.status === 404) EventBus.$emit('OFFER_EXPIRED_EVENT')
        } finally {
            runtimeStore.SET_BOOKING_ACTIVE(false)
        }
    }

    function SET_PREFERENCES_RESPONSE(searchResponse: any) {
        const {mealTypes, seatAllocations}: any = {...searchResponse}
        flightPreferences.value = {mealTypes, seatAllocations}
    }

    async function loadFlightPreferences(offerKey: any) {
        try {
            const rs = await appInstance.$api.flightPreferences.get({offerKey})
            SET_PREFERENCES_RESPONSE(rs)
        } catch (e) {
            flightPreferences.value = null
        }
    }

    // TODO [P3] Deprecated - Need delete after changes for all products
    const isOffersExpired = computed(() => {
        return () => false
    })

    const basketItem = computed(() => {
        return (offerKey: any) => _getBasketItem(offerKey)
    })

    const hasOffers = computed(() => {
        return !!searchResponse.value.offers.length
    })

    const hasScheduleOffers = computed(() => {
        return searchScheduleResponse.value.routes.every(route => !!route.offers.length)
    })

    const hasAvailableOffers = computed(() => {
        return !!availabilityResponse.value.offers.length
    })

    function SET_BOOKING_ADDITIONAL_OPTIONS(options: any) {
        if (Object.keys(options).length) {
            bookingAdditionalOptions.value = options
        }
    }

    async function loadBookingAdditionalOptions(offerKey: any) {
        try {
            const options = await appInstance.$api.additionalFlightOptions.get({offerKey})
            SET_BOOKING_ADDITIONAL_OPTIONS(options)
        } catch (error) {
            console.error(error)
        }
    }

    function clearBookingAdditionalOptions() {
        bookingAdditionalOptions.value = []
        mealTypesSeatAllowance.value = []
        frequentFlyerCard.value = []
    }

    async function getExtraServiceAdditionalOptions(offerKey: any) {
        try {
            const res = await appInstance.$api.additionalFlightOptions.get({offerKey})
            SET_BOOKING_ADDITIONAL_OPTIONS(res)
        } catch (error) {
            console.error(error)
        }
    }

    return {
        ...useProductStore(searchResponse),
        searchMode,
        searchRequest,
        scheduleSearchRequest,
        availabilityRequest,
        searchResponse,
        searchScheduleResponse,
        availabilityResponse,
        sortFnName,
        searchExpirationTime,
        filters,
        scheduleFilters,
        availableOfferFilters,
        prepareBookRequest,
        prepareBookResponse,
        selectedScheduleItems,
        basket,
        bookingAdditionalOptions,
        flightPreferences,
        mealTypesSeatAllowance,
        frequentFlyerCard,
        airlines,
        loadAirlines,
        ADD_TO_BASKET,
        REFRESH_BASKET_PRICE,
        SET_SEARCH_REQUEST,
        SET_SCHEDULE_SEARCH_REQUEST,
        SET_MEAL_TYPE_SEAT_ALLOWANCE,
        DELETE_MEAL_TYPE_SEAT_ALLOWANCE,
        SET_FREQUENT_FLYER_CARD_NUMBER,
        DELETE_FREQUENT_FLYER_CARD_TYPE,
        SET_FREQUENT_FLYER_CARD_TYPE,
        SET_AVAILABILITY_REQUEST,
        SET_SEARCH_MODE,
        RESET_FILTERS,
        SET_FILTER,
        SET_SCHEDULE_FILTER,
        RESET_SCHEDULE_FILTERS,
        SET_AVAILABLE_OFFER_FILTER,
        RESET_AVAILABLE_OFFER_FILTERS,
        SET_SORT,
        SET_SEARCH_EXPIRATION_TIME,
        SET_PREPARE_BOOK_REQUEST,
        SET_PREPARE_BOOK_REQUEST_PROP,
        SET_PREPARE_BOOK_RESPONSE,
        SET_SELECTED_SCHEDULE_ITEM,
        NEW_SEARCH,
        RESET,
        NEW_SCHEDULE_SEARCH,
        SET_SEARCH_SCHEDULE_RESPONSE,
        SET_AVAILABILITY_RESPONSE,
        prepareBook,
        SET_PREFERENCES_RESPONSE,
        loadFlightPreferences,
        SET_BOOKING_ADDITIONAL_OPTIONS,
        loadBookingAdditionalOptions,
        clearBookingAdditionalOptions,
        getExtraServiceAdditionalOptions,
        isOffersExpired,
        basketItem,
        hasOffers,
        hasScheduleOffers,
        hasAvailableOffers,
    }
})

// TODO [P3] Need check this HRM improvement. Relates with https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useFlightsStore, import.meta.hot))
